<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import DocumentsSection from '@/components/documents/DocumentsSection.vue'
import FinestAssistantPanel from '@/components/FinestAssistantPanel.vue'
import type { InlineEditableSelectOption } from '@/components/finest-field-grid/types'
import SectionTabs, { SectionTab } from '@/components/SectionTabs.vue'
import Household from '@/pages/investors/profile/Household.vue'
import PersonalData from '@/pages/investors/profile/PersonalData.vue'
import Professional from '@/pages/investors/profile/Professional.vue'
import { missingPerProfileTab, type ProfileTab } from '@/pages/investors/profile/tabs'
import type { ExtendedEnum } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import { Head } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import DocumentSectionData = App.Data.Documents.DocumentSectionData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData
import NaturalPersonData = App.Data.User.NaturalPersonData

type SpouseData = NaturalPersonData & {
  has_marital_contract?: boolean | null
  has_separate_assessment?: boolean | null
}

type ChildData = NaturalPersonData & {
  receives_child_support?: boolean | null
}

const props = defineProps<{
  person: NaturalPersonData
  spouse?: SpouseData | null
  children?: ChildData[]
  salutationOptions: ExtendedEnum[]
  genderOptions: ExtendedEnum[]
  maritalStatusOptions: ExtendedEnum[]
  employmentOptions: ExtendedEnum[]
  nationalityOptions: InlineEditableSelectOption[]
  profileCompletion: number
  fieldStates: FieldStateMap
  documents: DocumentSectionData
  documentTypeOptions: DocumentTypeOptionData[]
}>()

const { t } = useI18n()

const activeTab = ref<ProfileTab>('person')

const missingPerTab = computed(() => missingPerProfileTab(props.fieldStates))

const tabs = computed<SectionTab<ProfileTab>[]>(() => [
  { label: t('tabs.person'), value: 'person', count: missingPerTab.value.person },
  { label: t('tabs.household'), value: 'household', count: missingPerTab.value.household },
  { label: t('tabs.professional'), value: 'professional', count: missingPerTab.value.professional },
  { label: t('tabs.documents'), value: 'documents', icon: 'pi pi-file', trailing: true },
])
</script>

<template>
  <Head :title="t('heading')" />
  <AppContextPanel>
    <FinestAssistantPanel :completion="profileCompletion" />
  </AppContextPanel>
  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <div class="flex items-center gap-2">
      <h1 class="page-title">{{ t('heading') }}</h1>
      <PageTitleHelp :text="t('subtitle')" />
    </div>
  </div>

  <SectionTabs v-model="activeTab" :tabs="tabs" :aria-label="t('heading')" />

  <PersonalData
    v-if="activeTab === 'person'"
    :person="person"
    :field-states="fieldStates"
    :salutation-options="salutationOptions"
    :gender-options="genderOptions"
    :marital-status-options="maritalStatusOptions"
    :nationality-options="nationalityOptions" />

  <Household
    v-else-if="activeTab === 'household'"
    :person="person"
    :spouse="spouse"
    :children="children"
    :gender-options="genderOptions" />

  <Professional
    v-else-if="activeTab === 'professional'"
    :person="person"
    :field-states="fieldStates"
    :employment-options="employmentOptions" />

  <DocumentsSection v-else :section="documents" :type-options="documentTypeOptions" />
</template>

<i18n>
{
  "de": {
    "eyebrow": "Meine Basis",
    "heading": "Profil",
    "subtitle": "Deine persönlichen Angaben und dein Haushalt – einmal pflegen, bei jeder Finanzierungsanfrage automatisch aktuell mitgeschickt.",
    "tabs": {
      "person": "Person & Kontaktdaten",
      "household": "Haushalt & Familie",
      "professional": "Beruflich",
      "documents": "Dokumente"
    }
  },
  "en": {
    "eyebrow": "My Base",
    "heading": "Profile",
    "subtitle": "Your personal details and household – maintain once, automatically kept up to date with every financing request.",
    "tabs": {
      "person": "Person & Contact details",
      "household": "Household & Family",
      "professional": "Employment",
      "documents": "Documents"
    }
  }
}
</i18n>
